import { Stack } from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { ShiftDiscoverySearchMode } from "@src/appV2/OpenShifts/ShiftAction/api/useClaimShift";
import { isRebrandingEnabled } from "@src/appV2/redesign/system/ui-theme";
import { USER_EVENTS } from "@src/constants";

import { logEvent } from "../../lib/analytics";
import { Button } from "../components/Button";
import { SHIFT_DISCOVERY_LIST_PATH, SHIFT_DISCOVERY_MAP_PATH } from "./paths";
import { useIsShiftDiscoverySearchMode } from "./useShiftDiscoverySearchMode";

export const SHIFT_DISCOVERY_SEARCH_MODE_SWITCHER_SPACING_HEIGHT = 10;
export const SHIFT_DISCOVERY_SEARCH_MODE_SWITCHER_SPACING_BOTTOM = 3;

interface ShiftDiscoverySearchModeSwitcherProps {
  children?: React.ReactNode;
}

export function ShiftDiscoverySearchModeSwitcher(props: ShiftDiscoverySearchModeSwitcherProps) {
  const { children } = props;

  const isMapMode = useIsShiftDiscoverySearchMode(ShiftDiscoverySearchMode.MAP);

  return (
    <Stack
      direction="row"
      justifyContent="center"
      alignItems="center"
      spacing={3}
      sx={(theme) => ({
        position: "absolute",
        zIndex: theme.zIndex.appBar,
        left: 0,
        right: 0,
        bottom: theme.spacing(SHIFT_DISCOVERY_SEARCH_MODE_SWITCHER_SPACING_BOTTOM),
        height: theme.spacing(SHIFT_DISCOVERY_SEARCH_MODE_SWITCHER_SPACING_HEIGHT),
        margin: "0 auto",
      })}
    >
      {isMapMode ? (
        <Button
          aria-label="View List"
          size="medium"
          variant="contained"
          startIconType={isRebrandingEnabled() ? "list" : undefined}
          sx={(theme) => ({
            boxShadow: theme.shadows[3],
            ...(isRebrandingEnabled() && {
              backgroundColor: theme.palette.background.tertiary,
              color: theme.palette.text.primary,
              "& svg": { color: theme.palette.icon?.primary },
              "&:hover, &:focus": {
                backgroundColor: theme.palette.background.tertiary,
              },
            }),
          })}
          href={`${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_LIST_PATH}`}
        >
          View List
        </Button>
      ) : (
        <Button
          aria-label="View Map"
          size="medium"
          variant="contained"
          startIconType={isRebrandingEnabled() ? "map-paper" : undefined}
          sx={(theme) => ({
            boxShadow: theme.shadows[3],
            ...(isRebrandingEnabled() && {
              backgroundColor: theme.palette.background.tertiary,
              color: theme.palette.text.primary,
              "& svg": { color: theme.palette.icon?.primary },
              "&:hover, &:focus": {
                backgroundColor: theme.palette.background.tertiary,
              },
            }),
          })}
          href={`${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_MAP_PATH}`}
          onClick={() => {
            logEvent(USER_EVENTS.VIEW_OPEN_SHIFT_MAP, { trigger: "switchToMapView" });
          }}
        >
          View Map
        </Button>
      )}

      {children}
    </Stack>
  );
}
